import type { FC, ReactNode } from 'react'
import {
  Box,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import type { BffCrmPersonCareKeySavingsRetrieveResponse } from '@fireflyhealth/api'

import { EmptyState } from '~/shared/ui'
import type { PatientUser } from '~/shared/types'
import { useGetPersonCareKeySavings } from './PersonCareKeySavingsService'

import CareKeyIcon from './CareKeyIcon'

// Server-owned shapes — reuse the generated response types rather than re-declaring them.
type CareKeySavings = BffCrmPersonCareKeySavingsRetrieveResponse
type SavingsClaim = CareKeySavings['claims'][number]
type MissedSavingsProvider = CareKeySavings['missedSavingsProviders'][number]

interface CareKeySavingsProps {
  patient: PatientUser
}

const CURRENCY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

/**
 * Money arrives as a decimal string so no precision is lost in transit.
 *
 * Formatted rather than just prefixed with "$": a saving can legitimately be negative — the server
 * surfaces those instead of clamping them, so questionable inputs stay visible — and "$-5.00" reads
 * wrong where "-$5.00" reads right. Grouping matters too, since a member-year of specialist visits
 * can reach four figures. A value that is not a number is shown as sent rather than as "$NaN".
 */
const asCurrency = (amount: string) => {
  const value = Number(amount)
  return Number.isFinite(value) && amount.trim() !== '' ? CURRENCY.format(value) : `$${amount}`
}

/**
 * Identity for a rollup row. The server groups on the NPI when the claim carries one and on the
 * provider's name otherwise, because a payer can leave the NPI blank; keying on the NPI alone here
 * would collide two distinct blank-NPI providers back together in React.
 */
const providerKey = (provider: MissedSavingsProvider) =>
  provider.renderingNpi || `name:${provider.providerName}`

const StatTile: FC<{ label: string; value: string; testId: string }> = ({
  label,
  value,
  testId,
}) => (
  <Paper variant="outlined" sx={{ flex: 1, p: 2, borderRadius: '12px' }} data-testid={testId}>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.75rem', fontWeight: 600, mt: 0.5 }}>{value}</Typography>
  </Paper>
)

const SectionTable: FC<{ title: string; headers: string[]; children: ReactNode }> = ({
  title,
  headers,
  children,
}) => (
  <Box sx={{ mt: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
      {title}
    </Typography>
    <Paper variant="outlined" sx={{ borderRadius: '12px', overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {headers.map(header => (
              <TableCell key={header}>{header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{children}</TableBody>
      </Table>
    </Paper>
  </Box>
)

/**
 * "Not used" reads as a reproach, and on a visit with the member's own Firefly care team there is
 * nothing to reproach: a Care Key steers a member out to a network specialist, so there was nowhere
 * to steer them, and the visit already cost them nothing. Which case this is comes from the server.
 */
const CareKeyCell: FC<{ claim: SavingsClaim }> = ({ claim }) => {
  if (claim.hasCareKey) {
    return <Chip size="small" color="success" label="Used" />
  }
  if (claim.isFireflyProvider) {
    return <Chip size="small" variant="outlined" label="Firefly visit" />
  }
  return <Chip size="small" variant="outlined" label="Not used" />
}

/**
 * One analyzed visit. Only one of the two savings figures is meaningful per row — realized when the
 * visit used a Care Key, potential when it did not — so the row shows the one that applies rather
 * than a column of zeroes beside it.
 */
const ClaimRow: FC<{ claim: SavingsClaim }> = ({ claim }) => (
  <TableRow data-testid={`care-key-savings-claim-${claim.claimNumber}`}>
    <TableCell>{claim.dateOfService}</TableCell>
    <TableCell>{claim.providerName}</TableCell>
    <TableCell>
      <CareKeyCell claim={claim} />
    </TableCell>
    <TableCell>{asCurrency(claim.actualCostShare)}</TableCell>
    <TableCell>{asCurrency(claim.deductibleAmount)}</TableCell>
    <TableCell>
      {asCurrency(claim.hasCareKey ? claim.realizedSavings : claim.potentialSavings)}
      {/* The counterfactual is approximate when the benefit is coinsurance-based, so the cost
          share is a percentage of the allowed amount rather than a flat copay. Deductible
          dollars no longer set this — they are their own column, and they never enter a
          saving. The server decides; the tab only surfaces it. */}
      {claim.isEstimate && <Chip size="small" variant="outlined" label="Est." sx={{ ml: 1 }} />}
    </TableCell>
    <TableCell>
      {claim.isCareKeyCandidate ? (
        <Chip size="small" color="info" icon={<CareKeyIcon />} label="Candidate" />
      ) : null}
    </TableCell>
  </TableRow>
)

const ProviderRow: FC<{ provider: MissedSavingsProvider }> = ({ provider }) => (
  <TableRow data-testid={`care-key-savings-provider-${providerKey(provider)}`}>
    <TableCell>{provider.providerName}</TableCell>
    <TableCell>{provider.renderingNpi}</TableCell>
    <TableCell>{provider.visitCount}</TableCell>
    <TableCell>{asCurrency(provider.potentialSavingsTotal)}</TableCell>
    <TableCell>
      {provider.isCareKeyCandidate ? (
        <Chip size="small" color="info" icon={<CareKeyIcon />} label="Candidate" />
      ) : null}
    </TableCell>
  </TableRow>
)

const CareKeySavingsSkeleton: FC = () => (
  <Box sx={{ p: 3 }} data-testid="care-key-savings-loading">
    <Box sx={{ display: 'flex', gap: 2 }}>
      {[0, 1, 2, 3].map(index => (
        <Skeleton key={index} variant="rounded" height={92} sx={{ flex: 1 }} />
      ))}
    </Box>
    <Skeleton variant="rounded" height={220} sx={{ mt: 3 }} />
  </Box>
)

/**
 * What a Care Key was worth to this member, and what it could have been.
 *
 * Gated at the nav entry by the per-user `care_key_savings.crm_tab` flag, so the tab is absent
 * rather than empty for anyone without it — there is no in-component check here.
 *
 * Every figure, including whether a visit was a Care Key candidate, is computed by the BFF. The
 * "Candidate" chip is deliberately not "Eligible": it means the provider was recommended and in
 * network on a waiver-eligible benefit, which is a proxy — an actual Care Key still requires a
 * navigator-created steerage. It carries the member app's Care Key mark so the idea is
 * recognisable across surfaces, but keeps the word, because the app's bare mark asserts the
 * stronger "Care Key Eligible" and this screen cannot promise that.
 */
const CareKeySavings: FC<CareKeySavingsProps> = ({ patient }) => {
  const personId = patient.person?.id
  const { data, isPending: isLoading, error } = useGetPersonCareKeySavings({ personId })

  if (isLoading) {
    return <CareKeySavingsSkeleton />
  }

  if (error) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="Couldn't load Care Key savings"
        caption="Something went wrong fetching this member's Care Key savings. Please try again."
      />
    )
  }

  const claims = data?.claims ?? []
  const missedSavingsProviders = data?.missedSavingsProviders ?? []

  if (claims.length === 0) {
    return (
      <EmptyState
        icon={<InfoOutlinedIcon />}
        title="No Care Key savings yet"
        caption="No specialist office visits have been analyzed for this member yet."
      />
    )
  }

  const summary = data?.summary

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <StatTile
          label="Realized savings"
          value={asCurrency(summary?.realizedSavingsTotal ?? '0.00')}
          testId="care-key-savings-realized-total"
        />
        <StatTile
          label="Potential savings"
          value={asCurrency(summary?.potentialSavingsTotal ?? '0.00')}
          testId="care-key-savings-potential-total"
        />
        <StatTile
          label="Visits with a Care Key"
          value={String(summary?.careKeyClaimCount ?? 0)}
          testId="care-key-savings-care-key-count"
        />
        <StatTile
          label="Missed opportunities"
          value={String(summary?.missedOpportunityClaimCount ?? 0)}
          testId="care-key-savings-missed-count"
        />
      </Box>

      {/* Stated rather than left to inference: a Care Key lowers cost share and does not waive
          deductible, so this is money the member owes either way and none of it is savable. */}
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>
        Savings are calculated on cost share. This member also paid{' '}
        {asCurrency(summary?.deductibleTotal ?? '0.00')} toward their deductible on these visits,
        which a Care Key would not have reduced.
      </Typography>

      <SectionTable
        title="Analyzed visits"
        headers={[
          'Date of service',
          'Provider',
          'Care Key',
          'Cost share',
          'Deductible',
          'Savings',
          '',
        ]}
      >
        {claims.map(claim => (
          <ClaimRow key={claim.claimNumber} claim={claim} />
        ))}
      </SectionTable>

      {missedSavingsProviders.length > 0 && (
        <SectionTable
          title="Missed savings by provider"
          headers={['Provider', 'NPI', 'Visits', 'Potential savings', '']}
        >
          {missedSavingsProviders.map(provider => (
            <ProviderRow key={providerKey(provider)} provider={provider} />
          ))}
        </SectionTable>
      )}
    </Box>
  )
}

export default CareKeySavings
